import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DecisionTrack } from "@/constants/decision-track";
import { INSTALLMENT_OPTIONS } from "@/constants/payroll-installments";
import { JSX } from "react";

import { BatchApprovalState } from "../../hooks/use-batch-approval";
import {
  BATCH_APPROVAL_ACK_ALL_TEST_ID,
  BATCH_APPROVAL_APPLY_ALL_TEST_ID,
  BATCH_TRACK_SECTION_TEST_ID,
} from "../../utils/batch-approval-test-ids";
import { BATCH_APPROVAL_LABELS, DECISION_LABELS } from "../../utils/messages";
import { TRACK_SPEC, TrackSpec } from "../../utils/track-spec";
import { BatchItem } from "../shared/batch-action-modal";
import { BatchApprovalRow } from "./batch-approval-row";
import { BatchSimpleRow } from "./batch-simple-row";

type RowRenderer = (
  item: BatchItem,
  approval: BatchApprovalState
) => JSX.Element | null;

const identityRow: RowRenderer = (item, approval) => {
  const row = approval.rows.get(item.id);

  if (!row) return null;

  return (
    <BatchApprovalRow
      row={row}
      item={item}
      onInstallments={(value: number): void =>
        approval.setInstallments(item.id, value)
      }
      onCpf={(value: string): void => approval.setCpf(item.id, value)}
      onChosen={(value: string): void =>
        void approval.chooseCandidate(item.id, value)
      }
      onAckRule={(value: boolean): void => approval.ackRule(item.id, value)}
      onAckEmployment={(cpf: string): void =>
        approval.ackEmployment(item.id, cpf)
      }
      onSearch={(): void => void approval.search(item.id)}
    />
  );
};

const plainRow: RowRenderer = item => (
  <BatchSimpleRow id={item.id} item={item} />
);

const ROW_BY_IDENTITY_NEED: Record<string, RowRenderer> = {
  true: identityRow,
  false: plainRow,
};

interface BatchTrackSectionProps {
  track: DecisionTrack;
  items: BatchItem[];
  approval: BatchApprovalState;
}

export function BatchTrackSection({
  track,
  items,
  approval,
}: BatchTrackSectionProps): JSX.Element {
  const spec: TrackSpec = TRACK_SPEC[track];
  const renderRow: RowRenderer =
    ROW_BY_IDENTITY_NEED[String(spec.requiresIdentity)];

  return (
    <section
      data-testid={BATCH_TRACK_SECTION_TEST_ID(track)}
      className="flex flex-col gap-3"
    >
      <div className="flex flex-col gap-0.5">
        <h3 className="text-sm font-semibold text-[#613F4F]">
          {spec.sectionTitle}
        </h3>
        <p className="text-xs text-gray-500">{spec.sectionHint}</p>
      </div>

      {spec.requiresInstallments && (
        <div className="flex flex-wrap items-center justify-end gap-2 rounded-md border bg-muted/30 p-3">
          <span className="text-xs text-gray-500">
            {DECISION_LABELS.INSTALLMENTS_BATCH_APPLY_ALL}
          </span>
          <Select
            onValueChange={(value: string): void =>
              approval.setAllInstallments(Number(value))
            }
          >
            <SelectTrigger
              className="h-8 w-[80px]"
              data-testid={BATCH_APPROVAL_APPLY_ALL_TEST_ID}
              aria-label={DECISION_LABELS.INSTALLMENTS_BATCH_APPLY_ALL}
            >
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {INSTALLMENT_OPTIONS.map(
                (option: number): JSX.Element => (
                  <SelectItem key={option} value={`${option}`}>
                    {`${option}x`}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
        </div>
      )}

      {spec.requiresIdentity && (
        <label className="flex cursor-pointer items-center gap-2 rounded-md border bg-muted/30 p-3 text-sm">
          <input
            type="checkbox"
            data-testid={BATCH_APPROVAL_ACK_ALL_TEST_ID}
            checked={approval.allRuleAcked}
            onChange={(event): void =>
              approval.ackAllRules(event.target.checked)
            }
          />
          <span>{BATCH_APPROVAL_LABELS.ACK_ALL}</span>
        </label>
      )}

      <div className="flex flex-col gap-3">
        {items.map(
          (item: BatchItem): JSX.Element => (
            <div key={item.id}>{renderRow(item, approval)}</div>
          )
        )}
      </div>
    </section>
  );
}
